import { api, auth } from "salesive-api-axios";
import { setSessionIdentity } from "../components/chat/chatUtils.js";
import {
    applySessionMirror,
    clearLocalAuthArtifacts,
} from "./sessionState.js";
import { isAutomatedBrowser } from "./automatedBrowser.js";

/**
 * Ask the store API who the current cookie belongs to and mirror that into
 * the in-memory session flags the widget UI reads.
 *
 * Returns:
 *   "user"  — signed-in shopper (show chat, hide Welcome Back)
 *   "ghost" — anonymous session (show Welcome Back unless skipped)
 *   "none"  — no usable session cookie
 */
export async function syncSessionFromServer() {
    try {
        const res = await auth.session();
        const data = res?.data?.data ?? res?.data ?? {};
        const authenticated = data.authenticated === true || !!data.user;
        const role = data.role || data.user?.role || null;

        if (!authenticated) {
            // No cookie (or expired). Clear any transitional localStorage keys
            // so isLoggedIn() cannot keep returning true after a theme logout.
            clearLocalAuthArtifacts();
            applySessionMirror({
                active: false,
                user: false,
                serverMode: "none",
            });
            setSessionIdentity(null);
            return "none";
        }

        const isUser = !!role && role !== "ghost";
        if (!isUser) {
            clearLocalAuthArtifacts();
        }
        applySessionMirror({
            active: true,
            user: isUser,
            serverMode: isUser ? "user" : "ghost",
        });

        const user = data.user;
        if (user && typeof user === "object") {
            // Always cache id for shoppers (including ghosts) so message
            // attribution can match sender._id even when role is missing.
            setSessionIdentity({
                id: user._id || user.id || null,
                email: user.email || null,
            });
        }

        return isUser ? "user" : "ghost";
    } catch (error) {
        const status = error?.response?.status;
        if (status === 401 || status === 403) {
            clearLocalAuthArtifacts();
            applySessionMirror({
                active: false,
                user: false,
                serverMode: "none",
            });
            setSessionIdentity(null);
            return "none";
        }
        throw error;
    }
}

const GC_COOKIE_RE = /(?:^|;\s*)sv_gc=([^;]+)/;

/** Strip the one-time code from the URL and the sv_gc cookie. */
function clearGoogleHandoff() {
    try {
        document.cookie = "sv_gc=; path=/; max-age=0; samesite=lax";
        const url = new URL(window.location.href);
        if (url.searchParams.has("gc")) {
            url.searchParams.delete("gc");
            window.history.replaceState({}, "", url.toString());
        }
    } catch {
        // Non-fatal: the code is single-use and expires server-side.
    }
}

/** Resolve the post-login target, refusing anything that isn't same-origin. */
function safeNext() {
    try {
        const raw = new URLSearchParams(window.location.search).get("next");
        if (!raw) return "/";
        const url = new URL(raw, window.location.origin);
        return url.origin === window.location.origin
            ? `${url.pathname}${url.search}${url.hash}`
            : "/";
    } catch {
        return "/";
    }
}

/**
 * If the page carries the Google sign-in hand-off code (the `sv_gc` cookie
 * salesive-static-server sets from `?gc=`, or `?gc=` itself), trade it for the
 * real httpOnly `jwt` cookie. This runs on every storefront at widget boot, so
 * Google sign-in completes on any theme without a theme rebuild — the callback
 * lands the shopper on /login and this navigates them on to where they were.
 *
 * Returns true when a redirect is under way (caller should stop booting).
 */
async function redeemGoogleHandoff() {
    let code = null;
    try {
        code = new URLSearchParams(window.location.search).get("gc");
        if (!code) {
            const m = document.cookie.match(GC_COOKIE_RE);
            code = m ? decodeURIComponent(m[1]) : null;
        }
    } catch {
        return false;
    }
    if (!code) return false;

    try {
        await api.post(
            "auth/google/exchange",
            { code },
            { withCredentials: true },
        );
        // Valid code — the shopper just completed Google sign-in. The session
        // cookie is set now; leave /login for the page they came from.
        clearGoogleHandoff();
        window.location.replace(safeNext());
        return true;
    } catch {
        // Dead / expired / already-spent code: fall through to a normal boot.
        clearGoogleHandoff();
        return false;
    }
}

/**
 * Ensure there is a session cookie. Reuses an existing user/ghost cookie;
 * only calls createGhost when nothing is authenticated yet.
 */
export async function ensureShopperSession() {
    // Never mint a guest for a script (the widget itself does not start for
    // one; this covers anything that calls the session helpers directly).
    if (isAutomatedBrowser()) return "none";

    if (await redeemGoogleHandoff()) {
        return "user";
    }

    let mode = await syncSessionFromServer();
    if (mode === "none") {
        try {
            await auth.createGhost();
            mode = await syncSessionFromServer();
        } catch (err) {
            // Cookie may be blocked cross-site; still surface ghost UI rather
            // than leaving a stale "logged in" mirror in place.
            console.warn(
                "[SalesiveWidget] createGhost failed after empty session",
                err,
            );
            clearLocalAuthArtifacts();
            applySessionMirror({
                active: false,
                user: false,
                serverMode: "none",
            });
            mode = "none";
        }
    }
    return mode;
}
